import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { usePin } from '../context/PinContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  LayoutDashboard,
  Target,
  Boxes,
  Wrench,
  FileText,
  Heart,
  Settings,
  Search,
  Lock,
  Menu,
  X,
  Shield,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/firearms', label: 'Firearms', icon: Target },
  { path: '/ammunition', label: 'Ammunition', icon: Boxes },
  { path: '/accessories', label: 'Accessories', icon: Wrench },
  { path: '/documents', label: 'Documents', icon: FileText },
  { path: '/wishlist', label: 'Wishlist', icon: Heart },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Layout = ({ children, title, showSearch = false, onSearch, searchPlaceholder = "Search..." }) => {
  const { lock } = usePin();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleLock = () => {
    lock();
    navigate('/unlock');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col w-64 fixed h-screen border-r border-border ${theme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-card'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-border">
          <NavLink to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-8 h-8 bg-secondary rounded-sm flex items-center justify-center">
              <Shield className="w-5 h-5 text-secondary-foreground" />
            </div>
            <span className="font-bold text-lg tracking-tight text-foreground font-['Chivo']">ARMS ROOM</span>
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Lock App section */}
        <div className="p-4 border-t border-border">
          <button
            onClick={handleLock}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="lock-app-btn"
          >
            <Lock className="w-5 h-5" />
            <span>Lock App</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 h-16 flex items-center justify-between px-4 border-b border-border bg-background/80 backdrop-blur-md">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-muted rounded-sm"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-secondary" />
          <span className="font-bold text-foreground font-['Chivo']">ARMS ROOM</span>
        </div>
        <button
          onClick={handleLock}
          className="p-2 hover:bg-muted rounded-sm"
          data-testid="mobile-lock-btn"
        >
          <Lock className="w-5 h-5 text-muted-foreground" />
        </button>
      </header>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background/95 backdrop-blur-sm pt-16">
          <nav className="p-4 space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLock();
              }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-sm text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-lock-nav"
            >
              <Lock className="w-5 h-5" />
              <span>Lock App</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen flex flex-col">
        {/* Page Header */}
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-6 border-b border-border bg-background/80 backdrop-blur-md">
          <h1 className="text-xl font-bold tracking-tight text-foreground font-['Chivo'] uppercase">
            {title}
          </h1>
          
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="relative hidden sm:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={handleSearch}
                  className="pl-9 w-64 bg-background/50 border-input rounded-sm"
                  data-testid="search-input"
                />
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 flex-1">
          {children}
        </div>

        {/* Version Footer */}
        <footer className="py-4 px-6 text-center">
          <span className="text-xs text-muted-foreground/60">Version 1.1</span>
        </footer>
      </main>
    </div>
  );
};
